import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  LinearProgress,
  Chip,
  Paper,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Science as ScienceIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { crlApi } from '../../services/api';

const scoreColors = {
  easy: '#4caf50',
  moderate: '#ff9800',
  difficult: '#f44336',
  veryDifficult: '#e91e63',
};

function StatCard({ title, value, icon, color }) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" fontWeight="bold">
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: color ? `${color}20` : 'primary.light',
              p: 1.5,
              borderRadius: 2,
            }}
          >
            {React.cloneElement(icon, { sx: { color: color || 'primary.main', fontSize: 32 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await crlApi.getStats();
      setStats(response.data);
    } catch (err) {
      setError('Failed to load statistics. Make sure the API is running.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const processingPercent = stats?.total_letters && stats?.processed_letters
    ? Math.round((stats.processed_letters / stats.total_letters) * 100)
    : 0;

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        CRL Analysis Dashboard
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total CRL Letters"
            value={stats?.total_letters || 0}
            icon={<DescriptionIcon />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Processed with AI"
            value={stats?.processed_letters || 0}
            icon={<ScienceIcon />}
            color="#9c27b0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg Difficulty Score"
            value={stats?.avg_difficulty_score?.toFixed(1) || '-'}
            icon={<TrendingUpIcon />}
            color="#f57c00"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Processing Complete"
            value={`${processingPercent}%`}
            icon={<CheckCircleIcon />}
            color="#2e7d32"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Score Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="medium">
              Difficulty Score Distribution
            </Typography>
            {stats?.score_distribution?.length > 0 ? (
              <Box>
                {stats.score_distribution.map((item, idx) => (
                  <Box key={idx} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">{item.score_range}</Typography>
                      <Typography variant="body2" fontWeight="bold">{item.count}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((item.count / (stats.processed_letters || 1)) * 100, 100)}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: idx === 0 ? scoreColors.easy :
                                   idx === 1 ? scoreColors.moderate :
                                   idx === 2 ? scoreColors.difficult :
                                   scoreColors.veryDifficult,
                          borderRadius: 5,
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary">No score data available yet.</Typography>
            )}
          </Paper>
        </Grid>

        {/* Deficiency Types */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="medium">
              Deficiency Types Found
            </Typography>
            {stats?.deficiency_counts ? (
              <Box>
                {Object.entries(stats.deficiency_counts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, count]) => (
                    <Box key={type} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{type}</Typography>
                        <Typography variant="body2" fontWeight="bold">{count}</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min((count / (stats.processed_letters || 1)) * 100, 100)}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: type === 'New Trial Required' ? '#e91e63' : '#1976d2',
                            borderRadius: 4,
                          },
                        }}
                      />
                    </Box>
                  ))}
              </Box>
            ) : (
              <Typography color="text.secondary">No deficiency data available yet.</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom fontWeight="medium">
          Quick Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={() => navigate('/crl/letters')}
          >
            Explore CRL Letters
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/crl/letters?has_new_trial=true')}
          >
            View New Trial Required
          </Button>
        </Box>
      </Paper>

      {/* Score Guide */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="medium">
          Difficulty Score Guide
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Chip label="1-2" sx={{ bgcolor: scoreColors.easy, color: 'white', fontWeight: 'bold' }} />
              <Typography variant="body2">Minor labeling fixes (2-4 months)</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Chip label="2-3" sx={{ bgcolor: scoreColors.moderate, color: 'white', fontWeight: 'bold' }} />
              <Typography variant="body2">Single facility/minor CMC (6-12 months)</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Chip label="3-4" sx={{ bgcolor: scoreColors.difficult, color: 'white', fontWeight: 'bold' }} />
              <Typography variant="body2">Multiple CMC, human factors (12-18 months)</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Chip label="4-5" sx={{ bgcolor: scoreColors.veryDifficult, color: 'white', fontWeight: 'bold' }} />
              <Typography variant="body2">New Phase 3 trial required (3+ years)</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

export default Dashboard;
